import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import { Card } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import { Icon } from '@/components/icons';
import styles from './AdminDataImportPage.module.css';

interface ImportHistoryEntry {
  id: string;
  date: string;
  type: string;
  rowsImported: number;
  errors: number;
  status: 'SUCCESS' | 'FAILED' | 'PARTIAL';
}

interface ImportCategory {
  code: string;
  name: string;
  lastImport?: string;
  lastStatus?: string;
}

const CATEGORIES: ImportCategory[] = [
  { code: 'students', name: 'admin.dataImport.students' },
  { code: 'guardians', name: 'admin.dataImport.guardians' },
  { code: 'staff', name: 'admin.dataImport.staff' },
  { code: 'classes', name: 'admin.dataImport.classes' },
  { code: 'subjects', name: 'admin.dataImport.subjects' },
  { code: 'academicStructure', name: 'admin.dataImport.academicStructure' },
  { code: 'enrolments', name: 'admin.dataImport.enrolments' },
];

const STATUS_CLASSES: Record<string, string> = {
  SUCCESS: styles.statusSuccess,
  FAILED: styles.statusFailed,
  PARTIAL: styles.statusPartial,
};

function formatDate(ts?: string): string {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return ts;
  }
}

export function AdminDataImportPage() {
  const { t } = useTranslation();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importingCategory, setImportingCategory] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const historyKey = `admin-import-history-${institutionId}`;
  const { state: historyState, reload: reloadHistory } = useAsyncData<ImportHistoryEntry[]>(
    historyKey,
    () =>
      api.admin.dataImport.listJobs(institutionId) as unknown as Promise<ImportHistoryEntry[]>,
    !!institutionId,
  );

  const handleFileSelect = useCallback((categoryCode: string) => {
    setImportingCategory(categoryCode);
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !importingCategory) return;
    setSelectedFile(file);
    try {
      const reader = new FileReader();
      const b64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const job = (await api.admin.dataImport.upload(
        institutionId,
        importingCategory,
        file.name,
        b64,
      )) as unknown as { rowsImported?: number; errors?: number; status?: string };
      if (job.status === 'FAILED') {
        toast.error(t('admin.dataImport.uploadFailed'));
      } else if ((job.errors ?? 0) > 0) {
        toast.info(t('admin.dataImport.uploadPartial', { errors: job.errors }));
      } else {
        toast.success(t('admin.dataImport.uploadSuccess', { imported: job.rowsImported ?? 0 }));
      }
      reloadHistory();
    } catch {
      toast.error(t('admin.dataImport.uploadFailed'));
    } finally {
      setSelectedFile(null);
      setImportingCategory(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [importingCategory, institutionId, reloadHistory, t, toast]);

  const handleDownloadTemplate = useCallback(async (categoryCode: string) => {
    try {
      const template = await api.admin.dataImport.template(institutionId, categoryCode);
      const blob = new Blob([template.content], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = template.fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('admin.dataImport.templateFailed'));
    }
  }, [institutionId, t, toast]);

  if (historyState.status === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  if (historyState.status === 'error') {
    const error = historyState.error;
    return (
      <div className={styles.page}>
        <Card className={styles.emptyState}>
          <Icon name={error.isMissingEndpoint ? 'info' : 'error'} size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.dataImport.title')}</h2>
          <p className={styles.emptyStateBody}>
            {t(error.isMissingEndpoint ? 'platform.failure.missing.body' : 'platform.failure.generic.body')}
          </p>
          <button className={styles.retryButton} onClick={reloadHistory}>{t('common.retry')}</button>
        </Card>
      </div>
    );
  }

  const history = historyState.data || [];

  // Most recent job per category drives the "last import" line on each card.
  const lastImportByCategory = new Map<string, ImportHistoryEntry>();
  for (const entry of history) {
    if (!lastImportByCategory.has(entry.type)) lastImportByCategory.set(entry.type, entry);
  }

  return (
    <div className={styles.page}>
      <div className="page-header">
        <h1>{t('admin.dataImport.title')}</h1>
        <p className="page-subtitle">{t('admin.dataImport.subtitle')}</p>
      </div>

      <div className={styles.cardGrid}>
        {CATEGORIES.map((cat) => (
          <Card key={cat.code} className={styles.importCard}>
            <h3 className={styles.cardName}>{t(cat.name)}</h3>
            <span className={styles.lastImport}>
              {t('admin.dataImport.lastImport')}:{' '}
              {formatDate(lastImportByCategory.get(cat.code)?.date)}
            </span>
            <div className={styles.cardActions}>
              <button
                type="button"
                className={styles.templateBtn}
                onClick={() => handleDownloadTemplate(cat.code)}
              >
                {t('admin.dataImport.downloadTemplate')}
              </button>
              <button
                type="button"
                className={styles.uploadBtn}
                disabled={importingCategory === cat.code && !!selectedFile}
                onClick={() => handleFileSelect(cat.code)}
              >
                {importingCategory === cat.code && selectedFile
                  ? t('admin.dataImport.importing')
                  : t('admin.dataImport.uploadFile')}
              </button>
            </div>
          </Card>
        ))}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        className={styles.hiddenInput}
        onChange={handleFileChange}
      />

      <Card className={styles.historySection}>
        <h2 className={styles.historyTitle}>{t('admin.dataImport.history')}</h2>
        <div className="table-scroll">
          <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('admin.dataImport.historyDate')}</th>
              <th>{t('admin.dataImport.historyType')}</th>
              <th>{t('admin.dataImport.historyRows')}</th>
              <th>{t('admin.dataImport.historyErrors')}</th>
              <th>{t('admin.dataImport.historyStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 ? (
              <tr className={styles.noDataRow}>
                <td colSpan={5}>{t('admin.dataImport.noImports')}</td>
              </tr>
            ) : (
              history.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatDate(entry.date)}</td>
                  <td>{t(`admin.dataImport.${entry.type}`, entry.type)}</td>
                  <td>{entry.rowsImported.toLocaleString()}</td>
                  <td>{entry.errors > 0 ? entry.errors : '0'}</td>
                  <td>
                    <span className={`${styles.statusBadge} ${STATUS_CLASSES[entry.status] || styles.statusSuccess}`}>
                      {t(
                        `admin.dataImport.${
                          entry.status === 'SUCCESS'
                            ? 'importSuccess'
                            : entry.status === 'PARTIAL'
                              ? 'importPartial'
                              : 'importFailed'
                        }`,
                        entry.status,
                      )}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
